import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Container,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import loginbg from '../../Assets/login.png';
import config from '../../Config';

export default function LoginScreen() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await fetch(`${config.apiBaseUrl}/guest/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success && data.userData?.token) {
        localStorage.setItem('token', data.userData.token);
        localStorage.setItem('userEmail', data.userData.email);
        localStorage.setItem('userRole', data.userData.role);
        navigate('/home');
      } else {
        setErrorMessage(data.message || 'Login failed');
      }
    } catch (error) {
      setErrorMessage('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) return;

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await fetch(`${config.apiBaseUrl}/guest/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await response.json();
      if (response.ok && data.message) {
        setForgotMessage(data.message);
        setTimeout(() => {
          setIsForgotMode(false);
          setForgotMessage('');
        }, 3000);
      } else {
        setErrorMessage(data.message || 'Failed to send reset link');
      }
    } catch (error) {
      setErrorMessage('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: { xs: '100%', sm: 400 },
        margin: '0 auto',
        px: { xs: 2, sm: 0 },
        py: { xs: 1, sm: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: { xs: 1.5, sm: 2.5 },
          px: { xs: 1, sm: 2 },
          mt: { xs: 0.5, sm: 1 },
        }}
      >
        {isForgotMode && (
          <ArrowBackIosNewIcon
            onClick={() => {
              setIsForgotMode(false);
              setForgotMessage('');
              setForgotEmail('');
            }}
            sx={{ cursor: 'pointer', color: '#000', mr: 1 }}
          />
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#000',
            fontSize: { xs: '18px', sm: '20px' },
            flexGrow: 1,
            textAlign: isForgotMode ? 'left' : 'center',
          }}
        >
          {isForgotMode ? 'Forgot Password' : 'Login'}
        </Typography>
      </Box>

      <Container
        maxWidth="sm"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 1, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, sm: 4 } }}>
          <Box
            component="img"
            src={loginbg}
            alt="Login illustration"
            sx={{
              width: { xs: '120px', sm: '180px' },
              height: { xs: '110px', sm: '170px' },
              objectFit: 'contain',
            }}
          />
        </Box>

        {isForgotMode ? (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1.5, color: '#000', fontWeight: 400 }}>
                Enter your email to receive a reset link:
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="Enter your email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    height: '56px',
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#d0d0d0' },
                    '&.Mui-focused fieldset': { borderColor: '#2e7d7b' },
                  },
                  '& .MuiOutlinedInput-input': { padding: '12px 14px' },
                }}
              />
            </Box>

            <Button
              fullWidth
              variant="contained"
              disabled={!forgotEmail || loading}
              onClick={handleForgotPassword}
              sx={{
                backgroundColor: '#2e7d7b',
                color: 'white',
                py: 2,
                borderRadius: '28px',
                fontWeight: 600,
                fontSize: '16px',
                '&:hover': { backgroundColor: '#1e5a58' },
              }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>

            {forgotMessage && (
              <Typography sx={{ mt: 3, color: 'green', textAlign: 'center' }}>
                {forgotMessage}
              </Typography>
            )}

            {errorMessage && (
              <Typography sx={{ mt: 3, color: 'red', textAlign: 'center' }}>
                {errorMessage}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1.5, color: '#000' }}>
                Email
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    height: '56px',
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#d0d0d0' },
                    '&.Mui-focused fieldset': { borderColor: '#2e7d7b' },
                  },
                  '& .MuiOutlinedInput-input': { padding: '12px 14px' },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                  Password
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#999', cursor: 'pointer' }}
                  onClick={handleTogglePassword}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Typography>
              </Box>
              <TextField
                fullWidth
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    height: '56px',
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#d0d0d0' },
                    '&.Mui-focused fieldset': { borderColor: '#2e7d7b' },
                  },
                  '& .MuiOutlinedInput-input': { padding: '12px 14px' },
                }}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    sx={{
                      color: '#ccc',
                      mr: 1.5,
                      '&.Mui-checked': { color: '#000' },
                      '& .MuiSvgIcon-root': { fontSize: 18 },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    By logging in, I agree to the{' '}
                    <Link href="#" sx={{ color: '#000', textDecoration: 'underline' }}>
                      Terms of Use
                    </Link>{' '}
                    and{' '}
                    <Link href="#" sx={{ color: '#000', textDecoration: 'underline' }}>
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
            </Box>

            <Box>
              <Button
                fullWidth
                variant="contained"
                disabled={!email || !password || !agreedToTerms || loading}
                onClick={handleLogin}
                sx={{
                  backgroundColor: '#2e7d7b',
                  color: 'white',
                  py: 2,
                  borderRadius: '28px',
                  fontWeight: 600,
                  fontSize: '16px',
                  '&:hover': { backgroundColor: '#1e5a58' },
                  '&:disabled': { backgroundColor: '#ccc', color: '#999' },
                }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </Box>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography
                variant="body2"
                sx={{ color: '#2e7d7b', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  setIsForgotMode(true);
                  setErrorMessage('');
                }}
              >
                Forgot Password?
              </Typography>
            </Box>

            {errorMessage && (
              <Typography sx={{ mt: 2, color: 'red', textAlign: 'center' }}>
                {errorMessage}
              </Typography>
            )}
          </>
        )}
      </Container>

      <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
        <Box sx={{ width: 134, height: 5, backgroundColor: '#000', borderRadius: 2.5 }} />
      </Box>
    </Box>
  );
}
